import Decimal from 'decimal.js';
import { calculateQuotePrepayment, prepaymentInvoiceTaxRate, roundQuotePrepayment } from '../policies/quote.policy';
import { PrepaymentSetting } from '../settings/general.settings';
import { AdjustmentType, EmbeddedCode } from '../shared/code';
import { ItemSection, ItemSectionType, QuotePro } from './quote.pro';

/**
 * Quote domain calculator for EF V2 create/update and surface recompute.
 * Legacy quote editor keeps `QuoteCalculationDomain` and is not this module.
 *
 * Line math matches the legacy exclusive calculator: tax = sellEx * rate / 100.
 * Cost-only sections zero sell/adjustment before totals. Source is never mutated.
 */
export type QuoteCalculationSource = Pick<QuotePro, 'sections' | 'policy'> & {
  /** Tenant prepayment setting: the prepayment invoice's rate when the policy snapshot has no settings. */
  prepaymentSetting?: PrepaymentSetting | null;
};

export interface QuoteCalculationResult {
  sections: ItemSection[];
  totalBuy: number;
  totalSellEx: number;
  totalAdjustment: number;
  totalTax: number;
  totalSellInc: number;
  prepaymentEx: number;
  prepaymentTax: number;
  prepaymentInc: number;
}

export class QuoteCalculationDomain {

  static calculate(source: QuoteCalculationSource): QuoteCalculationResult {

    return calculateQuote(source);
  }
}

export function calculateQuote(source: QuoteCalculationSource): QuoteCalculationResult {

  const sections = (source?.sections ?? []).map(section => calculateSection(section));

  let totalBuy = new Decimal(0);
  let totalSellEx = new Decimal(0);
  let totalAdjustment = new Decimal(0);
  let totalTax = new Decimal(0);
  let totalSellInc = new Decimal(0);

  for (const section of sections) {
    totalBuy = totalBuy.plus(section.totalBuy ?? 0);
    totalSellEx = totalSellEx.plus(section.totalSellEx ?? 0);
    totalAdjustment = totalAdjustment.plus(section.totalAdjustment ?? 0);
    totalTax = totalTax.plus(section.totalTax ?? 0);
    totalSellInc = totalSellInc.plus(section.totalSellInc ?? 0);
  }

  const totals = {
    totalBuy: round2(totalBuy),
    totalSellEx: round2(totalSellEx),
    totalAdjustment: round2(totalAdjustment),
    totalTax: round2(totalTax),
    totalSellInc: round2(totalSellInc),
  };

  const prepayment = calculatePrepaymentTotals({ policy: source?.policy, ...totals } as QuotePro, source?.prepaymentSetting);

  return { sections, ...totals, ...prepayment };
}

function calculateSection(source: ItemSection | undefined): ItemSection {

  const costOnly = isCostOnlySection(source);
  const items = (source?.items ?? []).map(item => calculateItem(item, costOnly));

  let totalBuy = new Decimal(0);
  let totalSellEx = new Decimal(0);
  let totalAdjustment = new Decimal(0);
  let totalTax = new Decimal(0);
  let totalSellInc = new Decimal(0);

  for (const item of items) {
    totalBuy = totalBuy.plus(item.totalBuy ?? 0);
    totalSellEx = totalSellEx.plus(item.totalSellEx ?? 0);
    totalAdjustment = totalAdjustment.plus(item.totalAdjustment ?? 0);
    totalTax = totalTax.plus(item.totalTax ?? 0);
    totalSellInc = totalSellInc.plus(item.totalSellInc ?? 0);
  }

  return {
    ...(source ?? {} as ItemSection),
    costOnly,
    type: source?.type ?? (costOnly ? ItemSectionType.CostOnly : ItemSectionType.Items),
    items,
    totalBuy: round2(totalBuy),
    totalSellEx: round2(totalSellEx),
    totalAdjustment: round2(totalAdjustment),
    totalTax: round2(totalTax),
    totalSellInc: round2(totalSellInc),
  };
}

function calculateItem(source: EmbeddedCode | undefined, costOnly: boolean): EmbeddedCode {

  const item: EmbeddedCode = { ...(source ?? {} as EmbeddedCode) };

  if (costOnly) {
    item.sellPrice = 0;
    item.customSellPrice = false;
    item.adjustment = 0;
  }

  const quantity = requireFinite(item.quantity ?? 0, 'quantity');
  const sellPrice = requireFinite(costOnly ? 0 : (item.sellPrice ?? 0), 'sellPrice');
  const buyPrice = requireFinite(item.buyPrice ?? 0, 'buyPrice');
  const adjustment = requireFinite(costOnly ? 0 : (item.adjustment ?? 0), 'adjustment');
  const taxRate = requireFinite(item.saleTaxRate?.rate ?? 0, 'saleTaxRate.rate');
  const adjustmentType: AdjustmentType = item.adjustmentType === 'Percent' ? 'Percent' : 'Value';

  const totalBuy = round2(new Decimal(buyPrice).mul(quantity));
  const gross = round2(new Decimal(sellPrice).mul(quantity));
  const totalAdjustment = adjustmentType === 'Percent'
    ? round2(new Decimal(gross).mul(adjustment).div(100))
    : round2(adjustment);
  const totalSellEx = round2(new Decimal(gross).plus(totalAdjustment));
  const totalTax = round2(new Decimal(totalSellEx).mul(taxRate).div(100));
  const totalSellInc = round2(new Decimal(totalSellEx).plus(totalTax));

  item.totalBuy = totalBuy;
  item.totalAdjustment = totalAdjustment;
  item.totalSellEx = totalSellEx;
  item.totalTax = totalTax;
  item.totalSellInc = totalSellInc;
  item.adjustedSell = quantity === 0 ? 0 : round2(new Decimal(totalSellEx).div(quantity));

  return item;
}

function calculatePrepaymentTotals(quote: QuotePro, tenantPrepayment: PrepaymentSetting | null | undefined): Pick<QuoteCalculationResult, 'prepaymentEx' | 'prepaymentTax' | 'prepaymentInc'> {

  return roundQuotePrepayment(calculateQuotePrepayment(quote), prepaymentInvoiceTaxRate(quote.policy, tenantPrepayment));
}

function isCostOnlySection(section: ItemSection | undefined): boolean {

  return section?.costOnly === true || section?.type === ItemSectionType.CostOnly;
}

function requireFinite(value: number, field: string): number {

  const numeric = value as unknown;
  if (typeof numeric === 'string' && numeric.trim() === '') {
    return 0;
  }

  const n = Number(value);
  if (!Number.isFinite(n)) {
    throw new Error(`Quote calculation rejected non-finite ${field}`);
  }
  return n;
}

function round2(value: Decimal.Value): number {

  return new Decimal(value ?? 0).toDP(2).toNumber();
}
